import React, { useEffect, useState } from "react";
import styles from "./ConfirmDialog.module.css";

/**
 * Usage Example:
 *
 * ```javascript
 * const [confirmDialog, setConfirmDialog] = useState({
 *   isOpen: false,
 *   message: "",
 *   onConfirm: () => {},
 *   variant: "default", // 'default', 'warning', 'danger'
 *   title: "", // add title for modal (eg. Confirm Bid, Are you Sure )
 * });
 *
 * <ConfirmDialog
 *   isOpen={confirmDialog.isOpen}
 *   onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
 *   onConfirm={confirmDialog.onConfirm}
 *   title={confirmDialog.title}
 *   message={confirmDialog.message}
 *   variant={confirmDialog.variant}
 * />
 * ```
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
  disableButton = false,
  closeOnEscape = true,
  showConfirm = true,
}) => {
  const [loading, setLoading] = useState(false);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const dialogClasses = [
    styles.dialog,
    variant === "warning" ? styles.warning : "",
    variant === "danger" ? styles.danger : "",
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen && closeOnEscape) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      // document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={dialogClasses}>
        <div className={styles.header}>
          <h3>{title}</h3>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className={styles.content}>
          <p>{message}</p>
        </div>
        <div className={styles.footer}>
          <button className={styles.cancelButton} onClick={onClose}>
            {cancelText}
          </button>
          {showConfirm && (
            <button
              className={`${styles.confirmButton} ${styles[variant]}`}
              onClick={handleConfirm}
              disabled={loading || disableButton}
              autoFocus
            >
              {confirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
